import React, { Component } from 'react';
import styled from 'styled-components';

import Text from 'components/Text';
import { SIDEPANEL_STATES, RESOURCE_TYPES } from 'utils/appConstants';
import { setSidePanel, revertSidePanel } from 'reducers/sidePanel/actions';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

const BreadcrumbContainer = styled.div`
  box-sizing: border-box;
  padding: 10px 1.6em;
  display: flex;
  height: 2.2em;
  z-index: 1;
  -webkit-transform: translate3d(0, 0, 0);
  transform: translate3d(0, 0, 0);

  &::-webkit-scrollbar {
    height: 5px;
    width: 5px;
  }

  &::-webkit-scrollbar-track {
    background-color: #e5e7e8;
    border: 1px solid #d8d8d8;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(13, 21, 31, 0.4);
  }
`;

const TrailButton = styled.button`
  display: flex;
  background: transparent;
  background-color: transparent;
  cursor: pointer;
  outline: none;
`;

const TrailText = styled(Text)`
  color: ${({ isActive }) => (isActive ? 'rgba(13, 21, 31, 0.8)' : '#71777D')};
`;

const Arrows = styled(TrailText)`
  padding: 0 0.3em;
`;

class MapBreadcrumbs extends Component {
  constructor(props) {
    super(props);

    this.state = {
      trail: []
    };
  }

  isSidePanelReadPanel = () => {
    const { sidePanel } = this.props;
    if (sidePanel.state === SIDEPANEL_STATES.READ) {
      return true;
    }
  };

  isSidePanelEntity = entity => {
    const { sidePanel } = this.props;
    if (sidePanel.entity === entity) {
      return true;
    }
  };

  isCurrentTab = entity => {
    const { currentTab } = this.props;
    if (currentTab === entity) {
      return true;
    }
  };

  /**
   * Check if we switched maps,
   * or the side panel was closed,
   * or if we clicked something new on the tree map (reflected in sidepanel change and selectedOnTreeMap property),
   * or if we created a new resource while on that resource's map page
   */
  componentDidUpdate(prevProps) {
    const { sidePanel, indeNodes, aggregators } = this.props;
    const { displaySidePanel, selectedOnTreeMap } = sidePanel;
    const { trail } = this.state;

    //If we switch tabs, or if we close the sidepanel on the topology page, clear the breadcrumbs
    if (
      !this.isCurrentTab(prevProps.currentTab) ||
      (trail.length &&
        !displaySidePanel &&
        this.isCurrentTab(RESOURCE_TYPES.TOPONODE))
    ) {
      this.setState({ trail: [] });
    }
    //If the sidepanel has changed from one read panel to another read panel...
    else if (prevProps.sidePanel !== sidePanel && this.isSidePanelReadPanel()) {
      //If a user clicked something on the map, recompile breadcrumbs (selectedOnTreeMap property).
      //Or, when a user creates a new resource, the sidepanel is opened to that resource. If
      //user is on that resource map (indenode, aggregator), recompile breadcrumbs.
      if (
        selectedOnTreeMap ||
        (this.isSidePanelEntity(RESOURCE_TYPES.TOPONODE) &&
          this.isCurrentTab(RESOURCE_TYPES.TOPONODE) &&
          this.isSidePanelReadPanel()) ||
        (this.isSidePanelEntity(RESOURCE_TYPES.INDENODE) &&
          this.isCurrentTab(RESOURCE_TYPES.INDENODE) &&
          this.isSidePanelReadPanel()) ||
        (this.isSidePanelEntity(RESOURCE_TYPES.AGGREGATOR) &&
          this.isCurrentTab(RESOURCE_TYPES.AGGREGATOR) &&
          this.isSidePanelReadPanel())
      ) {
        this.compileBreadcrumbs(prevProps.sidePanel);
      }
      //Check if the resouce got deleted
      else if (
        prevProps.sidePanel.entity === RESOURCE_TYPES.INDENODE &&
        this.isCurrentTab(RESOURCE_TYPES.INDENODE)
      ) {
        const indeNodeData = indeNodes.find(
          indeNode => indeNode.indeNodeId === prevProps.sidePanel.id
        );
        if (!indeNodeData) {
          return this.setState({ trail: [] });
        }
      } else if (
        prevProps.sidePanel.entity === RESOURCE_TYPES.AGGREGATOR &&
        this.isCurrentTab(RESOURCE_TYPES.AGGREGATOR)
      ) {
        const aggregatorData = aggregators.find(
          aggregator => aggregator.aggregatorId === prevProps.sidePanel.id
        );
        if (!aggregatorData) {
          return this.setState({ trail: [] });
        }
      }
    }
  }

  //TODO: Make this and the createTrailWithParents() functions more generic.
  //Although the resource types have to be handled in specific ways, it could
  //be made more general. An idea is to define an object beforehand that we can look through
  //and determine how to handle each resource type as we arrive.
  //A VERY ROUGH idea would be something like this:

  //const howToHandleObjects = [
  //  {
  //    expectedResourceType: RESOURCE_TYPES.TOPONODE,
  //    parentPropertyName: 'parentTopoNodeId',
  //    parentPropertyType: RESOURCE_TYPES.TOPONODE,
  //    maxPossibleNestedLevels: Math.inifinity
  //  },
  //  {
  //    expectedResourceType: RESOURCE_TYPES.DEVICE,
  //    ...
  //  }
  //];
  //Another idea: change all 'id' properties in the redux store to just 'id' rather than indeNodeId, aggregatorId, etc.
  //Another idea: make an appConstant with the RESOURCE_TYPE matching its redux store name, id name, etc. Similar to the first idea
  //listed in this TODO.

  /**
   * Creates the breadcrumbs trail in an array stored in the state trail[].
   * The first item in the array should be the farthest parent, and the last item should be the current sidepanel that is open.
   * If there is not a sidepanel open, this should return an empty array.
   * @param {object} previousSidePanel (optional) - If this parameter is passed in, the compiling
   * of the breadcrumb trail should happen from the this data instead of the current sidepanel.
   * This will be done if we are on the indemap or aggmap pages. This function will check to see
   * if the sidepanel is not opened and we are on the indemap or aggmap page. If so, keep that last
   * opened indenode/aggregator breadcrumb item until they click another one.
   */
  compileBreadcrumbs = previousSidePanel => {
    const { sidePanel } = this.props;
    let { entity, id, sfdi } = sidePanel;
    const { displaySidePanel } = sidePanel;

    //If no panel is open and we're on the topomap, there should be no breadcrumbs.
    if (!displaySidePanel && this.isCurrentTab(RESOURCE_TYPES.TOPONODE)) {
      return this.setState({ trail: [] });
    }
    //If we are on the indemap or aggmap, display the last sidePanel. As soon as a user clicks
    //one item in the list, the breadcrumbs will persist with that.
    else if (!displaySidePanel && previousSidePanel) {
      entity = previousSidePanel.entity;
      id = previousSidePanel.id;
    }

    //Gather data and compile a trail[]
    const trail = this.createTrailWithParents(entity, id, sfdi, []);

    //Reverse the trail order
    trail.reverse();

    //Set the state
    this.setState({ trail });
  };

  /**
   * Creates and returns a trail array that holds our breadcrumbs. This looks for the parent of the
   * entity/id combo in the parameters, and calls itself recursively until we reach the end.
   * @param {string} entity - one of the RESOURCE_TYPES that the sidepanel uses.
   * @param {string} id - the id of the entity
   * @return {array} trail - this is the final array with the order of the first element being
   * the first element put into this function, and the last element being the farthest parent/ancestor.
   */
  createTrailWithParents = (entity, id, sfdi, trail) => {
    const { currentTab, topoNodes, devices, sidePanel } = this.props;
    const { breadcrumbs } = sidePanel;

    const newTrail = trail.slice();

    //Add entity and id to current trail
    newTrail.push({ entity, id });

    //Find the parent
    if (entity === RESOURCE_TYPES.TOPONODE) {
      const topoNode = topoNodes.find(topoNode => topoNode.topoNodeId === id);
      if (topoNode.parentTopoNodeId) {
        return this.createTrailWithParents(
          RESOURCE_TYPES.TOPONODE,
          topoNode.parentTopoNodeId,
          null,
          newTrail
        );
      } else {
        return newTrail;
      }
    } else if (entity === RESOURCE_TYPES.DEVICE) {
      const device = devices.find(device => device.sfdi === sfdi);
      if (this.isCurrentTab(RESOURCE_TYPES.TOPONODE)) {
        if (device.topoNodeId) {
          return this.createTrailWithParents(
            RESOURCE_TYPES.TOPONODE,
            device.topoNodeId,
            null,
            newTrail
          );
        } else {
          return newTrail;
        }
      } else if (
        this.isCurrentTab(RESOURCE_TYPES.INDENODE) ||
        this.isCurrentTab(RESOURCE_TYPES.AGGREGATOR)
      ) {
        if (breadcrumbs.length > 0) {
          newTrail.push({
            entity: currentTab,
            id: breadcrumbs[breadcrumbs.length - 1].id
          });
        }
        return newTrail;
      }
    } else if (
      entity === RESOURCE_TYPES.INDENODE ||
      entity === RESOURCE_TYPES.AGGREGATOR
    ) {
      return newTrail;
    }
  };

  /**
   * When a user clicks an item in the breadcrumbs, open the sidepanel for that item.
   * The last item in the breadcrumb could be a device or the currentTab/current resource type, so that needs to be checked.
   * @param {string} index - the index of the item in the trail[] array that was selected by a user action.
   */
  selectTrailNode = async index => {
    const {
      setSidePanel,
      revertSidePanel,
      sidePanel: { breadcrumbs }
    } = this.props;
    const { trail } = this.state;

    const newTrail = trail.slice(0, index + 1);
    this.setState({ trail: newTrail });

    //Check if we're on the indeMap or aggMap pages. If so, we want to keep the previous
    //entity in the breadcrumbs if there is one. So we will revert the sidepanel back to the previous,
    //then immediately set it to the newly chosen entity.
    const keepBreadcrumbs =
      this.isCurrentTab(RESOURCE_TYPES.INDENODE) ||
      this.isCurrentTab(RESOURCE_TYPES.AGGREGATOR);
    if (keepBreadcrumbs && breadcrumbs.length > 0) {
      await revertSidePanel();
    }
    setSidePanel({
      entity: trail[index].entity,
      state: SIDEPANEL_STATES.READ,
      id: trail[index].id,
      keepBreadcrumbs,
      selectedOnTreeMap: true
    });
  };

  render() {
    const { trail } = this.state;

    return (
      <BreadcrumbContainer id="breadcrumb-container">
        {trail.map((item, i) => {
          let isActive = i === trail.length - 1;
          return (
            <React.Fragment key={`breadcrumb-trail-${i}`}>
              <TrailButton onClick={event => this.selectTrailNode(i)}>
                <TrailText isActive={isActive}>{item.id}</TrailText>
                {isActive ? null : <Arrows>&#187;</Arrows>}
              </TrailButton>
            </React.Fragment>
          );
        })}
      </BreadcrumbContainer>
    );
  }
}

const mapStateToProps = state => {
  return {
    topoNodes: state.topoNode.topoNodes,
    indeNodes: state.indeNode.indeNodes,
    aggregators: state.aggregator.aggregators,
    devices: state.device.devices,
    sidePanel: state.sidePanel
  };
};

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      setSidePanel,
      revertSidePanel
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(MapBreadcrumbs);
